/**
 * Client-side automation/headless-browser signal collection.
 *
 * Gathers cheap, non-PII browser traits that differ between real interactive
 * browsers and headless/automation runtimes (Puppeteer, Playwright, Selenium).
 * The backend fraud gate scores these alongside server-side User-Agent checks —
 * nothing here is trusted on its own (a client can lie), it only raises suspicion.
 */

export interface ClientSignals {
  webdriver: boolean;
  plugins: number;
  languages: number;
  hasChrome: boolean;
  userAgent: string;
  webglVendor: string | null;
  hardwareConcurrency: number;
  isMobile: boolean;
  permissionMismatch: boolean;
}

const MOBILE_UA_PATTERN = /Android|iPhone|iPad|iPod|Mobile/i;

function readWebglVendor(): string | null {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return null;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return null;
    return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string;
  } catch {
    return null;
  }
}

/**
 * Headless Chrome reports Notification.permission === 'denied' while the
 * Permissions API reports 'prompt' for notifications — a contradiction a real
 * browser never produces. A true result is a strong headless tell.
 */
async function detectPermissionMismatch(): Promise<boolean> {
  try {
    if (typeof Notification === 'undefined' || !navigator.permissions) return false;
    const status = await navigator.permissions.query({
      name: 'notifications' as PermissionName,
    });
    return Notification.permission === 'denied' && status.state === 'prompt';
  } catch {
    return false;
  }
}

export async function collectClientSignals(): Promise<ClientSignals> {
  const nav = navigator;
  return {
    webdriver: Boolean(nav.webdriver),
    plugins: nav.plugins?.length ?? 0,
    languages: nav.languages?.length ?? 0,
    hasChrome: typeof (window as unknown as { chrome?: unknown }).chrome !== 'undefined',
    userAgent: nav.userAgent || '',
    webglVendor: readWebglVendor(),
    hardwareConcurrency: nav.hardwareConcurrency ?? 0,
    isMobile: MOBILE_UA_PATTERN.test(nav.userAgent || ''),
    permissionMismatch: await detectPermissionMismatch(),
  };
}
